import { useState } from "react";

import {
  describeCondition,
  isShadowRule,
  LABEEB_CATALOG,
  labeebPaged,
  labeebRulePath,
  labeebRulesPath,
  RULE_EFFECTS,
  RULE_MODES,
  type LabeebCatalog,
  type LabeebRule,
  type LabeebRuleDocument,
} from "../../api/labeeb";
import { LoadMore } from "../LoadMore";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { labeebPage, usePagedList } from "../../hooks/usePagedList";
import { useResource } from "../../hooks/useResource";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { ResourceView } from "../states";
import { Badge, Card, Empty, Notice, Table, Td, Th } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * This business's autonomy rules — what the assistant may do unattended.
 *
 * Effect and mode are the two fields an operator changes here. Conditions,
 * scope and subject stay as stored: a half-built condition editor would
 * write a rule the operator did not mean. The whole document goes back on
 * every save, with only the field they changed.
 *
 * WHAT AN OPERATOR NEEDS FROM THIS TABLE, in order:
 *
 *   - which rules EXIST, because with no enforcing rule every write needs
 *     approval and reads never do. An empty list is a meaningful answer, not
 *     a gap, so it says that rather than showing nothing.
 *   - whether each one DECIDES anything. A `shadow` rule is evaluated and
 *     recorded as what it would have done, and changes nothing — it is also
 *     how a rule is retired, since this API has no delete. A shadow rule read
 *     as live is the single most misleading thing this table could do, so it
 *     is badged, not merely labelled in a column.
 *   - what it decides and on what. `effect` is the decision; strictest
 *     matching rule wins (deny > approve > auto).
 *
 * `version` is shown because every change sends it back as
 * `expected_version`, so it is the number an operator quotes when a write is
 * refused for being stale.
 */
export function LabeebRules({ config, tenantId }: { config: AppConfig; tenantId: string }) {
  const rules = usePagedList<LabeebRule>(
    config,
    labeebPaged(labeebRulesPath(tenantId)),
    labeebPage,
  );
  const catalog = useResource<LabeebCatalog>(config, LABEEB_CATALOG);
  const catalogReady = catalog.state.kind === "ready";
  let catalogVersion: string | undefined;
  let effects: readonly string[] = RULE_EFFECTS;
  if (catalog.state.kind === "ready") {
    catalogVersion = catalog.state.data.model_version;
    const listed = catalog.state.data.values?.rule_effects;
    if (listed && listed.length > 0) effects = listed;
  }
  const s = rules.state;

  return (
    <Card title="Autonomy rules" note="What the assistant may do without asking">
      {/* A 404 here means Labeeb has no record of this business — it never
          creates a phantom tenant, so this is "not registered", not "no
          rules". Saying "no rules" would imply the assistant is running
          unconstrained for a merchant Labeeb has never seen. */}
      <ResourceView
        state={s}
        what="autonomy rules"
        onRetry={rules.reload}
        notFound={
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Labeeb has no record of this merchant, so it holds no rules for them. A business
            appears once somebody at it signs in.
          </p>
        }
      />

      {/* The effect and mode controls refuse a rule whose model is not the
          catalog's. That check cannot run until this read has succeeded, so
          a failed catalog is said here — once — rather than left as an empty
          Change column that looks like the rules cannot be changed. */}
      {!catalogReady && (
        <div className={s.kind === "ready" ? "mb-3" : "mt-3"}>
          <ResourceView state={catalog.state} what="the catalog" onRetry={catalog.reload} />
        </div>
      )}

      {s.kind === "ready" &&
        (() => {
          const items = s.data.rows;

          if (items.length === 0) {
            return (
              <>
                <Empty>No autonomy rules{s.data.next ? " on this page" : ""}.</Empty>
                {/* THE DEFAULT IS NOT "ANYTHING GOES". Without an enforcing
                    rule every write needs approval, which is the safe end of
                    the range — but an operator reading an empty table could
                    conclude either, so it is stated. A cursor can still sit
                    behind an empty page, so "none" and "none yet loaded" stay
                    different sentences. */}
                {!s.data.next && (
                  <p className="mt-2 text-xs/5 text-slate-500 dark:text-slate-400">
                    With no enforcing rule, every write needs approval. Reads never do.
                  </p>
                )}
                <LoadMore
                  shown={0}
                  noun="rules"
                  hasMore={s.data.next !== null}
                  loading={rules.loadingMore}
                  error={rules.moreError}
                  onMore={rules.loadMore}
                />
              </>
            );
          }

          const enforcing = items.filter((r) => !isShadowRule(r)).length;

          return (
            <>
              {enforcing === 0 && (
                <div className="mb-3">
                  <Notice tone="amber" title="Nothing here is enforced">
                    Every rule below is observing only, so every write still needs approval.
                  </Notice>
                </div>
              )}

              <Table
                head={
                  <tr>
                    <Th>Rule</Th>
                    <Th>Applies to</Th>
                    <Th>Decides</Th>
                    <Th>Mode</Th>
                    <Th>Triggers</Th>
                    {catalogReady && <Th>Change</Th>}
                    <Th right>Version</Th>
                  </tr>
                }
              >
                {items.map((r) => (
                  <RuleRow
                    key={r.rule_id ?? JSON.stringify(r.rule)}
                    config={config}
                    tenantId={tenantId}
                    rule={r}
                    catalogReady={catalogReady}
                    catalogVersion={catalogVersion}
                    effects={effects}
                    onSaved={rules.reload}
                  />
                ))}
              </Table>

              <p className="mt-3 text-xs/5 text-slate-500 dark:text-slate-400">
                The strictest matching rule wins: deny beats approve beats auto. A rule is
                retired by setting it to observing — there is no delete.
              </p>
              <LoadMore
                shown={items.length}
                noun="rules"
                hasMore={s.data.next !== null}
                loading={rules.loadingMore}
                error={rules.moreError}
                onMore={rules.loadMore}
              />
            </>
          );
        })()}
    </Card>
  );
}

/** One rule. Its conditions are what make it specific, so they are shown. */
function RuleRow({
  config,
  tenantId,
  rule,
  catalogReady,
  catalogVersion,
  effects,
  onSaved,
}: {
  config: AppConfig;
  tenantId: string;
  rule: LabeebRule;
  catalogReady: boolean;
  catalogVersion: string | undefined;
  effects: readonly string[];
  onSaved: () => void;
}) {
  const d = rule.rule;
  const ops = d?.scope?.ops ?? [];
  const conditions = d?.conditions ?? [];
  const shadow = isShadowRule(rule);

  return (
    <tr>
      <Td>
        <span className="ref">{rule.rule_id ?? d?.id ?? "—"}</span>
        {/* The rule author's own sentence, which is usually a better
            explanation than the scope can be. */}
        {d?.reason && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{d.reason}</p>
        )}
      </Td>
      <Td>
        <span className="ref">{d?.scope?.target ?? "—"}</span>
        {ops.length > 0 && (
          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
            {ops.join(", ")}
          </span>
        )}
        {/* `branches: null` means every branch, which is NOT the same as an
            empty list — and it is the wider of the two, so it is said. */}
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {d?.scope?.branches == null
            ? "all branches"
            : d.scope.branches.length === 0
              ? "no branches"
              : `${d.scope.branches.length} branch(es)`}
        </p>
        {conditions.length > 0 && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            when {conditions.map(describeCondition).join(" · ")}
          </p>
        )}
      </Td>
      <Td>
        {d?.effect === "deny" ? (
          <Badge tone="red">deny</Badge>
        ) : d?.effect === "approve" ? (
          <Badge tone="amber">approve</Badge>
        ) : d?.effect === "auto" ? (
          <Badge>auto</Badge>
        ) : (
          <span className="text-xs text-slate-500 dark:text-slate-400">{d?.effect ?? "—"}</span>
        )}
      </Td>
      <Td>
        {shadow ? (
          <div>
            <Badge tone="amber">observing only</Badge>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">not enforced</p>
          </div>
        ) : (
          <Badge>enforce</Badge>
        )}
      </Td>
      <Td>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {(d?.triggers ?? []).join(", ") || "—"}
        </span>
      </Td>
      {catalogReady && (
        <Td>
          <RuleEdit
            config={config}
            tenantId={tenantId}
            rule={rule}
            catalogReady={catalogReady}
            catalogVersion={catalogVersion}
            effects={effects}
            onSaved={onSaved}
          />
        </Td>
      )}
      <Td right>
        <span className="tabular-nums">{rule.version ?? "—"}</span>
      </Td>
    </tr>
  );
}

/**
 * Change a rule's effect or mode. The stored document is sent back whole.
 *
 * A rule written for a different model than the catalog names is refused
 * here, before a request, because the API would answer 422 and the operator
 * would have typed a reason for nothing.
 */
function RuleEdit({
  config,
  tenantId,
  rule,
  catalogReady,
  catalogVersion,
  effects,
  onSaved,
}: {
  config: AppConfig;
  tenantId: string;
  rule: LabeebRule;
  catalogReady: boolean;
  catalogVersion: string | undefined;
  effects: readonly string[];
  onSaved: () => void;
}) {
  const document = rule.rule;
  const ruleId = rule.rule_id ?? document?.id;
  const write = useWrite(config);
  const [effect, setEffect] = useState<string>(document?.effect ?? RULE_EFFECTS[0]);
  const [mode, setMode] = useState<string>(document?.mode ?? RULE_MODES[0]);
  const [confirming, setConfirming] = useState(false);

  if (!catalogReady || !document || !ruleId) return null;

  const model = document.model_version;
  if (!catalogVersion || !model || model !== catalogVersion) {
    return (
      <p className="max-w-xs text-xs/5 text-slate-500 dark:text-slate-400">
        Written for model {model ?? "an unnamed model"}. The catalog's model is{" "}
        {catalogVersion ?? "unnamed"}. This rule cannot be changed until those match.
      </p>
    );
  }

  const dirty = effect !== (document.effect ?? "") || mode !== (document.mode ?? "");
  const version = rule.version;
  const busy = write.state.kind === "submitting";

  function submit(reason: string) {
    if (version === undefined || !document || !ruleId) return;
    const next: LabeebRuleDocument = {
      ...document,
      id: ruleId,
      effect: effect as LabeebRuleDocument["effect"],
      mode: mode as LabeebRuleDocument["mode"],
    };
    void write
      .submit(labeebRulePath(tenantId, ruleId), {
        method: "PATCH",
        body: JSON.stringify({ expected_version: version, reason, rule: next }),
      })
      .then((r) => {
        if (!r) return;
        onSaved();
      });
    setConfirming(false);
  }

  return (
    <div>
      <Gate need="settings:write">
        <div className="flex flex-col gap-1">
          <select
            aria-label={`Effect for ${ruleId}`}
            className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-900"
            value={effect}
            onChange={(e) => setEffect(e.target.value)}
          >
            {effects.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label={`Mode for ${ruleId}`}
            className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-900"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            {RULE_MODES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {dirty && (
            <Button disabled={busy || version === undefined} onClick={() => setConfirming(true)}>
              {`Save ${ruleId}…`}
            </Button>
          )}
        </div>
      </Gate>

      {write.state.kind === "done" && (
        <div className="mt-2">
          <WriteResult title="Rule saved" onDismiss={write.reset}>
            <p className="text-sm">
              Effect is {effect}, mode is {mode}. The rest of the rule is unchanged.
            </p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onSaved} />

      <ConfirmDialog
        open={confirming}
        title={`Save ${ruleId}`}
        confirmLabel="Save"
        requireReason
        reasonHint="e.g. DA-1700, retire by observing"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
        effect={
          mode === "shadow"
            ? "This rule will be evaluated and recorded, and it will enforce nothing."
            : `Matching actions will be decided ${effect}. The strictest matching rule still wins.`
        }
      >
        <p>
          Only the effect and the mode change. Conditions, scope and who it applies to stay as
          stored.
        </p>
      </ConfirmDialog>
    </div>
  );
}
